import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { IconSearch, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
// @ts-ignore
import { Avatar, Badge, Button, InputGroup } from 'dsm-react-lib'
import {
  IconCircleCheckFilled,
  IconCirclePlus,
  IconPuzzle,
  IconLayoutGrid,
  IconLayoutList
} from '@tabler/icons-react'
import { Tooltip } from 'react-tooltip'
import { useFetch } from '../../../../_hooks/useFetch'
import mapper from '../../../../_endpoints'
import Skeleton from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { setAgentDetails } from '../../../../_redux/actions/agentActions'
import EmptyContent from '../../Teams/EmptyContent'
import { getInitials } from '../../../../utility/Utils'
import { normalizeSkillNames } from './skillUtils'

interface Skill {
  name: string
  displayName: string
  description: string
  source: string
  category: string
  version?: string
  iconUrl?: string | null
  hasResources: boolean
  hasInProcessScripts: boolean
  fileScriptsExecutable: boolean
  scopeType?: string
}

interface SkillCategory {
  id: string
  displayName: string
  description?: string
}

interface SkillsProps {
  setStep: (step: 'details' | 'tools' | 'dataSources') => void
}

const getCanonicalSkillKey = (value: string): string => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '')
}

const getSkillKeyVariants = (value: string): string[] => {
  const trimmed = value.trim().toLowerCase()
  const hyphen = trimmed.replace(/[\s_]+/g, '-')
  const space = trimmed.replace(/[-_]+/g, ' ')
  const canonical = getCanonicalSkillKey(trimmed)
  return Array.from(new Set([trimmed, hyphen, space, canonical]))
}

const SkillsSkeletonLoader = () => (
  <div className="w-full pt-2">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="d-flex align-items-center border-bottom py-[8px]">
        <div className="ms-3 flex-1">
          <Skeleton height={14} width={300} className="animated-skeleton" />
          <Skeleton height={12} width={450} className="animated-skeleton" />
        </div>
        <div className="ms-2 mr-3">
          <Skeleton width={28} height={28} className="animated-skeleton" />
        </div>
      </div>
    ))}
  </div>
)

const Skills: React.FC<SkillsProps> = ({ setStep }) => {
  const dispatch = useDispatch()
  const { agent } = useSelector((state: any) => state.agentReducer)

  const [isLoadingSkills, fetchSkills] = useFetch('nexus')
  const [, fetchCategories] = useFetch('nexus')

  const [skills, setSkills] = useState<Skill[]>([])
  const [categories, setCategories] = useState<SkillCategory[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')

  // Derive selected skills directly from Redux — single source of truth
  const selectedSkills = useMemo(() => normalizeSkillNames(agent?.skills), [agent?.skills])

  const loadSkills = useCallback(async () => {
    const res = await fetchSkills(mapper.agents.getSkills)
    if (res?.skills) {
      setSkills(res.skills)
    }
  }, [])

  const loadCategories = useCallback(async () => {
    const res = await fetchCategories(mapper.agents.getSkillCategories)
    if (res?.categories) {
      setCategories(res.categories)
    }
  }, [])

  useEffect(() => {
    loadSkills()
    loadCategories()
  }, [])

  // Build a display name lookup from the categories endpoint, keyed by various possible matches
  const categoryDisplayMap = useMemo(() => {
    const map: Record<string, string> = {}
    categories.forEach((cat) => {
      map[cat.id?.toLowerCase()] = cat.displayName
      map[cat.displayName?.toLowerCase()] = cat.displayName
    })
    return map
  }, [categories])

  const filteredSkills = useMemo(() => {
    return skills.filter((skill) => {
      const isEnterprise = skill.scopeType?.toLowerCase() === 'enterprise'
      const matchesSearch =
        !searchTerm ||
        skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skill.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory =
        selectedCategory === 'All' ||
        skill.category?.toLowerCase() === selectedCategory.toLowerCase() ||
        (categoryDisplayMap[skill.category?.toLowerCase()] || '').toLowerCase() ===
          selectedCategory.toLowerCase()
      return isEnterprise && matchesSearch && matchesCategory
    })
  }, [skills, searchTerm, selectedCategory])

  const toggleSkill = (skillName: string) => {
    const skillNameVariants = getSkillKeyVariants(skillName)

    const hasSelectedSkill = selectedSkills.some((selected) =>
      getSkillKeyVariants(selected).some((variant) => skillNameVariants.includes(variant))
    )

    const updated = hasSelectedSkill
      ? selectedSkills.filter(
          (selected) =>
            !getSkillKeyVariants(selected).some((variant) => skillNameVariants.includes(variant))
        )
      : [...selectedSkills, skillName]

    dispatch(setAgentDetails({ ...agent, skills: updated }))
  }

  const isSkillSelected = (skillName: string) => {
    const skillNameVariants = getSkillKeyVariants(skillName)
    return selectedSkills.some((selected) =>
      getSkillKeyVariants(selected).some((variant) => skillNameVariants.includes(variant))
    )
  }

  const getSkillInitials = (skillName: string) => {
    return getInitials(skillName.replace(/-/g, ' ')).slice(0, 2)
  }

  const handleNext = () => {
    setStep('dataSources')
  }

  const handlePrevious = () => {
    setStep('details')
  }

  return (
    <>
      {/* Search and View Controls */}
      <div className="flex items-center justify-between mb-1">
        <div className="d-flex w-full mt-md-0 mt-1">
          <InputGroup
            addon={<IconSearch size={14} />}
            addonPosition="start"
            addonType="merged"
            placeholder="Search for skills..."
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-end mx-2 gap-25">
          <button
            onClick={() => setViewMode('grid')}
            className={`h-fit !p-[2px] bg-transparent border-none cursor-pointer ${
              viewMode === 'grid' ? 'theme-brand-accent-bold' : 'text-muted'
            }`}
            title="Grid view">
            <IconLayoutGrid
              size={26}
              className={viewMode === 'grid' ? 'theme-brand-accent-bold' : 'text-gray-400'}
            />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`h-fit !p-[3px] bg-transparent border-none cursor-pointer ${
              viewMode === 'list' ? 'theme-brand-accent-bold' : 'text-muted'
            }`}
            title="List view">
            <IconLayoutList
              size={24}
              className={viewMode === 'list' ? 'theme-brand-accent-bold' : 'text-gray-400'}
            />
          </button>
        </div>
      </div>

      {/* Category filter badges */}
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-50 mb-1">
          <Badge
            label="All"
            type="pill"
            size="medium"
            color={selectedCategory === 'All' ? 'var(--color-primary)' : undefined}
            variant={selectedCategory === 'All' ? undefined : 'light'}
            className={`cursor-pointer me-50 mb-1 px-1 font-semibold ${
              selectedCategory === 'All'
                ? 'theme-badge-filter-active'
                : 'theme-filter-badge-inactive-primary'
            }`}
            onClick={() => setSelectedCategory('All')}
          />
          {categories.map((cat) => (
            <Badge
              key={cat.id}
              label={cat.displayName}
              type="pill"
              size="medium"
              color={selectedCategory === cat.displayName ? 'var(--color-primary)' : undefined}
              variant={selectedCategory === cat.displayName ? undefined : 'light'}
              className={`cursor-pointer me-50 mb-1 px-1 font-semibold ${
                selectedCategory === cat.displayName
                  ? 'theme-badge-filter-active'
                  : 'theme-filter-badge-inactive-primary'
              }`}
              onClick={() => setSelectedCategory(cat.displayName)}
            />
          ))}
        </div>
      )}

      {/* Skills List/Grid */}
      <div className="mb-1">
        {isLoadingSkills ? (
          <SkillsSkeletonLoader />
        ) : filteredSkills.length === 0 ? (
          <div className="flex justify-center py-5">
            <EmptyContent
              icon={<IconPuzzle size={40} stroke={1.5} color="var(--color-primary)" />}
              title={searchTerm ? 'No skills match your search' : 'No skills found'}
              description={
                searchTerm
                  ? 'Try adjusting your search term or clearing the filter.'
                  : 'There are no skills available in this category.'
              }
            />
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white">
            {filteredSkills.map((skill, index) => (
              <div key={skill.name}>
                <div className="flex items-center p-1 gap-2">
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <Avatar
                      size="36px"
                      content={skill.iconUrl || getSkillInitials(skill.name)}
                      className="shrink-0 !m-0"
                    />
                    <div className="min-w-0">
                      <h5 className="font-medium text-gray-900 flex items-center gap-1 mb-0">
                        {skill.displayName || skill.name.replace(/-/g, ' ')}
                        {skill.version && (
                          <span className="text-xs text-gray-400 font-normal">
                            v{skill.version}
                          </span>
                        )}
                      </h5>
                      <p
                        className="text-sm text-gray-500 line-clamp-1 mb-0 cursor-default"
                        data-tooltip-id="skill-desc-tooltip"
                        data-tooltip-content={skill.description}>
                        {skill.description}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <button
                      type="button"
                      title={isSkillSelected(skill.name) ? 'Remove skill' : 'Add skill'}
                      onClick={() => toggleSkill(skill.name)}
                      className="p-25 rounded-full bg-transparent hover:bg-gray-100 transition-colors">
                      {isSkillSelected(skill.name) ? (
                        <IconCircleCheckFilled size={26} color="var(--color-primary)" />
                      ) : (
                        <IconCirclePlus size={26} color="var(--color-primary)" stroke={1.5} />
                      )}
                    </button>
                  </div>
                </div>
                {index < filteredSkills.length - 1 && (
                  <div className="border-b border-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {filteredSkills.map((skill) => (
              <div key={skill.name} className="relative flex-1 min-w-[350px] max-w-[400px]">
                <div className="border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-shadow bg-white">
                  {/* Header row: avatar + title + connect button */}
                  <div className="flex items-center gap-1 mb-50">
                    <Avatar
                      size="36px"
                      content={skill.iconUrl || getSkillInitials(skill.name)}
                      className="shrink-0 !m-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 flex items-center gap-1 mb-0 truncate">
                        {skill.displayName || skill.name.replace(/-/g, ' ')}
                        {skill.version && (
                          <span className="text-xs text-gray-400 font-normal shrink-0">
                            v{skill.version}
                          </span>
                        )}
                      </h4>
                    </div>
                    <div className="shrink-0">
                      <button
                        type="button"
                        title={isSkillSelected(skill.name) ? 'Remove skill' : 'Add skill'}
                        onClick={() => toggleSkill(skill.name)}
                        className="p-25 rounded-full bg-transparent hover:bg-gray-100 transition-colors">
                        {isSkillSelected(skill.name) ? (
                          <IconCircleCheckFilled size={26} color="var(--color-primary)" />
                        ) : (
                          <IconCirclePlus size={26} color="var(--color-primary)" stroke={1.5} />
                        )}
                      </button>
                    </div>
                  </div>
                  <p
                    className="text-sm text-gray-600 leading-relaxed line-clamp-3 min-h-[4.5em] mb-0 cursor-default"
                    data-tooltip-id="skill-desc-tooltip"
                    data-tooltip-content={skill.description}>
                    {skill.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Connected Skills Summary */}
        {selectedSkills.length > 0 && (
          <div className="my-1">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">
                {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="border-t border-gray-200 pt-1">
              <div className="flex flex-wrap gap-1">
                {selectedSkills.map((skillName) => (
                  <span
                    key={skillName}
                    className="flex items-center gap-1 px-1 py-25 bg-gray-100 text-gray-700 text-sm rounded-full capitalize">
                    {skillName.replace(/-/g, ' ')}
                    <div
                      onClick={() => toggleSkill(skillName)}
                      className="cursor-pointer rounded-full p-0.5 text-gray-700 hover:bg-gray-600">
                      ✕
                    </div>
                  </span>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 mt-1"></div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <Tooltip
        id="skill-desc-tooltip"
        className="!rounded-md !max-w-[400px] !text-sm !leading-relaxed z-[9999]"
      />
      <div className="flex justify-between items-center">
        <div></div>
        <div className="flex gap-1">
          <Button
            label="Previous step"
            onClick={handlePrevious}
            variant="outline"
            className="theme-accent-outline-btn"
            icon={<IconChevronLeft size={16} />}
          />
          <Button
            label="Next step"
            iconPosition="end"
            icon={<IconChevronRight size={16} />}
            color="primary"
            onClick={handleNext}
          />
        </div>
      </div>
    </>
  )
}

export default Skills
